import { useTranslation } from "../../i18n";

/**
 * Notification log viewer for a campaign.
 *
 * `log` is the current page from the backend; `offset`/`onOffsetChange` own
 * the paging. The backend reports the real total, which the previous
 * implementation replaced with "rows in this page (+1 if another page exists)"
 * — so a campaign with 5 000 sends displayed "201".
 *
 * The failed sends are rendered from the same response. They used to be
 * counted only ("доставлено 0 из 242") and the table stayed empty, which told
 * the operator nothing about the 241 recipients who were skipped.
 */
export default function CampaignLog({ log, offset = 0, onOffsetChange }) {
  const { t } = useTranslation();

  const items = log?.items ?? [];
  const failures = log?.failures ?? [];

  if (items.length === 0 && failures.length === 0) {
    return (
      <div className="text-c-gray-400 text-sm py-4 text-center">
        {t("campaign.noNotifications")}
      </div>
    );
  }

  const total = log.total ?? 0;
  const limit = log.limit ?? 200;
  const from = total === 0 ? 0 : offset + 1;
  const to = offset + items.length;
  const canPage = typeof onOffsetChange === "function";
  const canGoBack = canPage && offset > 0;
  const canGoForward = canPage && log.hasNext && to < total;

  return (
    <div className="space-y-4">
      {items.length > 0 && (
        <div>
          <div className="overflow-auto max-h-[300px]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-c-gray-400/30">
                  <th className="text-left py-2 px-2 text-c-gray-400">
                    {t("campaign.colCustomer")}
                  </th>
                  <th className="text-left py-2 px-2 text-c-gray-400">
                    {t("campaign.colPhone")}
                  </th>
                  <th className="text-left py-2 px-2 text-c-gray-400">
                    {t("campaign.colSentAt")}
                  </th>
                  <th className="text-left py-2 px-2 text-c-gray-400">
                    {t("campaign.colVariant")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((entry) => (
                  <tr key={entry.id} className="border-b border-c-gray-400/10">
                    <td className="py-2 px-2">
                      {entry.firstname || `#${entry.customerId}`}
                    </td>
                    <td className="py-2 px-2 text-c-gray-400">
                      {entry.phone || "—"}
                    </td>
                    <td className="py-2 px-2 text-c-gray-400">
                      {new Date(entry.sentAt).toLocaleString()}
                    </td>
                    <td className="py-2 px-2">{entry.variant || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between pt-2 text-c-gray-400 text-xs">
            <span>{t("campaign.logRange", { from, to, total })}</span>
            {canPage && (
              <span className="flex gap-1">
                <button
                  type="button"
                  className="px-2 py-1 rounded bg-c-gray-500 hover:bg-c-gray-400 disabled:opacity-40"
                  onClick={() => onOffsetChange(Math.max(0, offset - limit))}
                  disabled={!canGoBack}
                >
                  {t("campaign.logPrev")}
                </button>
                <button
                  type="button"
                  className="px-2 py-1 rounded bg-c-gray-500 hover:bg-c-gray-400 disabled:opacity-40"
                  onClick={() => onOffsetChange(offset + limit)}
                  disabled={!canGoForward}
                >
                  {t("campaign.logNext")}
                </button>
              </span>
            )}
          </div>
        </div>
      )}

      {failures.length > 0 && (
        <div>
          <h4 className="text-sm text-red-400 mb-1">
            {t("campaign.logFailuresTitle", {
              count: log.failuresTotal ?? failures.length,
            })}
          </h4>
          <div className="overflow-auto max-h-[240px]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-c-gray-400/30">
                  <th className="text-left py-2 px-2 text-c-gray-400">
                    {t("campaign.colCustomer")}
                  </th>
                  <th className="text-left py-2 px-2 text-c-gray-400">
                    {t("campaign.colPhone")}
                  </th>
                  <th className="text-left py-2 px-2 text-c-gray-400">
                    {t("campaign.colFailReason")}
                  </th>
                  <th className="text-left py-2 px-2 text-c-gray-400">
                    {t("campaign.colSentAt")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {failures.map((failure) => (
                  <tr
                    key={`failure-${failure.id}`}
                    className="border-b border-c-gray-400/10"
                  >
                    <td className="py-2 px-2">
                      {failure.firstname || `#${failure.customerId}`}
                    </td>
                    <td className="py-2 px-2 text-c-gray-400">
                      {failure.phone || "—"}
                    </td>
                    <td className="py-2 px-2 text-red-300">
                      {describeReason(failure.reason, t)}
                    </td>
                    <td className="py-2 px-2 text-c-gray-400">
                      {new Date(failure.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(log.failuresTotal ?? failures.length) > failures.length && (
            <p className="text-c-gray-400 text-xs pt-1">
              {t("campaign.logFailuresTruncated", {
                shown: failures.length,
                total: log.failuresTotal,
              })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** Map a stored failure reason to a readable label. */
function describeReason(reason, t) {
  if (!reason) return "—";
  const known = {
    "no device token": "campaign.failureReason.noDevice",
    "push disabled": "campaign.failureReason.disabled",
    "not delivered": "campaign.failureReason.notDelivered",
    "send failed": "campaign.failureReason.sendFailed",
    "render failed": "campaign.failureReason.renderFailed",
    "sent but not recorded": "campaign.failureReason.notRecorded",
  };
  const key = known[reason];
  return key ? t(key) : reason;
}
